import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { isAdmin, logout } from '../utils/auth';
import { FaDownload, FaUpload, FaUsers, FaChartLine, FaSignOutAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

function AdminDashboard() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard');
      return;
    }
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await api.get('/admin/students');
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/admin/upload-allowed-students', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(response.data.message);
      fetchStudents();
    } catch (error) {
      alert(`Erreur lors de l'upload: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.get('/admin/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `students_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#F8F4E3' }}>
        <div className="onomatopoeia">LOADING...</div>
      </div>
    );
  }

  const avgGlobalScore = students.length > 0 
    ? Math.round(students.reduce((sum, s) => sum + (s.stats?.globalScore || 0), 0) / students.length)
    : 0;

  const totalAttempts = students.reduce((sum, s) => sum + (s.stats?.totalAttempts || 0), 0);

  return (
    <div className="min-h-screen" style={{ background: '#F8F4E3' }}>
      {/* Header */}
      <header className="border-b-4 border-black" style={{ background: '#1B4965' }}>
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_775b1c44-a958-4f0a-89e7-e9533aa80b3a/artifacts/spmbyfn7_F06FBB44-F51A-4759-AAA6-D0C701D1C28A.jpeg" 
              alt="DailyGlobe" 
              className="w-12 h-12 object-contain"
            />
            <h1 className="text-2xl md:text-3xl comic-title text-white">Admin Dashboard</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button onClick={logout} className="comic-button comic-button-red" data-testid="logout-button">
              <FaSignOutAlt className="inline mr-2" /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
        >
          <div className="comic-panel bg-gradient-to-br from-blue-500 to-cyan-600 text-center" data-testid="total-students-panel">
            <FaUsers className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">{students.length}</h3>
            <p className="comic-text text-white text-xl">Étudiants</p>
          </div>
          
          <div className="comic-panel bg-gradient-to-br from-yellow-400 to-orange-500 text-center">
            <FaChartLine className="text-5xl mx-auto mb-2 text-white" />
            <h3 className="comic-title text-white text-4xl">{avgGlobalScore}</h3>
            <p className="comic-text text-white text-xl">Score Moyen</p>
          </div>
          
          <div className="comic-panel bg-gradient-to-br from-red-500 to-pink-600 text-center">
            <div className="text-5xl mx-auto mb-2 text-white">📝</div>
            <h3 className="comic-title text-white text-4xl">{totalAttempts}</h3>
            <p className="comic-text text-white text-xl">Tentatives Totales</p>
          </div>
        </motion.div>

        {/* Actions */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="comic-panel mb-8"
        >
          <h2 className="comic-title text-2xl mb-4" style={{ color: '#1B4965' }}>Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="csv-upload" className="comic-button comic-button-navy w-full block cursor-pointer text-center" data-testid="upload-csv-button">
                <FaUpload className="inline mr-2" />
                {uploading ? 'Upload...' : 'Upload CSV (Emails Autorisés)'}
              </label>
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploading}
              />
              <p className="text-sm mt-2 text-gray-600">
                Format: email, firstName, lastName, cohort
              </p>
            </div>
            
            <button
              onClick={handleExport}
              className="comic-button comic-button-red w-full"
              data-testid="export-button"
            >
              <FaDownload className="inline mr-2" />
              Exporter les Données (CSV)
            </button>
          </div>
        </motion.div>

        {/* Students Table */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="comic-panel overflow-x-auto"
        >
          <h2 className="comic-title text-2xl mb-4" style={{ color: '#1B4965' }}>Liste des Étudiants</h2>
          
          {students.length === 0 ? (
            <div className="speech-bubble text-center py-8">
              <p className="text-lg">Aucun étudiant enregistré pour le moment.</p>
            </div>
          ) : (
            <table className="w-full" data-testid="students-table">
              <thead className="bg-gray-100 border-b-3 border-black">
                <tr>
                  <th className="p-3 text-left comic-text">Nom</th>
                  <th className="p-3 text-left comic-text">Email</th>
                  <th className="p-3 text-left comic-text">Cohorte</th>
                  <th className="p-3 text-left comic-text">Dernière Connexion</th>
                  <th className="p-3 text-center comic-text">Score Global</th>
                  <th className="p-3 text-center comic-text">Quiz</th>
                  <th className="p-3 text-center comic-text">Grammaire</th>
                  <th className="p-3 text-center comic-text">Interview</th>
                  <th className="p-3 text-center comic-text">SEO</th>
                  <th className="p-3 text-center comic-text">Pitch</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, idx) => {
                  const lastLogin = student.lastLoginAt 
                    ? new Date(student.lastLoginAt).toLocaleDateString('fr-FR')
                    : '-';
                  
                  const scores = student.stats?.scoresByType || {};
                  
                  return (
                    <tr key={student.id} className="border-b border-gray-200 hover:bg-yellow-50">
                      <td className="p-3">
                        {student.firstName} {student.lastName}
                      </td>
                      <td className="p-3 text-sm">{student.email}</td>
                      <td className="p-3">{student.cohort || '-'}</td>
                      <td className="p-3 text-sm">{lastLogin}</td>
                      <td className="p-3 text-center font-bold" style={{ color: '#FFB703' }}>
                        {student.stats?.globalScore?.toFixed(1) || 0}
                      </td>
                      <td className="p-3 text-center">{scores.daily_quiz?.toFixed(1) || '-'}</td>
                      <td className="p-3 text-center">{scores.grammar?.toFixed(1) || '-'}</td>
                      <td className="p-3 text-center">{scores.interview?.toFixed(1) || '-'}</td>
                      <td className="p-3 text-center">{scores.seo?.toFixed(1) || '-'}</td>
                      <td className="p-3 text-center">{scores.pitch?.toFixed(1) || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </motion.div>
      </div>
    </div>
  );
}

export default AdminDashboard;
